import { deepCopy, shuffle } from "src/utils";

export interface TriviaQuestion {
  id: string;
  questionText: string;
  isCorrect: (guess: string) => boolean;
  photo: string;
  caption: string;
}

export function createTriviaQuestions(count: number): TriviaQuestion[] {
  const questionsPerRange = Math.floor(count / QUESTION_RANGES.length);
  const numberOfLeftoverQuestions = count % QUESTION_RANGES.length;

  const possibleQuestions: any = [];
  const chosenQuestions: TriviaQuestion[] = [];

  const copiedQuestionBank = deepCopy(QUESTION_BANK);
  for (let i = 0; i < QUESTION_RANGES.length; i++) {
    const currentQuestionRange = QUESTION_RANGES[i];
    const possibleQuestionsInRange = copiedQuestionBank.slice(
      currentQuestionRange.start,
      currentQuestionRange.end
    );

    // Mutates possibleQuestionsInRange, removing used questions.
    chosenQuestions.push(...addQuestionsToList(chosenQuestions.length, questionsPerRange, possibleQuestionsInRange));

    // Save the unused questions to potentially be used to fill out the leftover questions.
    possibleQuestions.push(...possibleQuestionsInRange);
  }

  // If there are any leftover questions needed to reach the goal count, add them now.
  if (numberOfLeftoverQuestions > 0) {
    chosenQuestions.push(...addQuestionsToList(chosenQuestions.length, numberOfLeftoverQuestions, possibleQuestions));
  }

  // shuffle the questions, including updating their question
  return shuffle(chosenQuestions);
}

function addQuestionsToList(
  numberOfQuestionsSoFar: number,
  numberOfQuestionsToAdd: number,
  possibleQuestions: {
    id: string;
    question: string;
    answer: string;
    photo: string;
    caption: string;
  }[]
) {
  const chosenQuestions: TriviaQuestion[] = [];
  for (let i = 0; i < numberOfQuestionsToAdd; i++) {
    const questionIndex = Math.floor(Math.random() * possibleQuestions.length);
    const questionData = possibleQuestions[questionIndex];
    possibleQuestions.splice(questionIndex, 1); // remove the question from the pool of available questions.

    const question: TriviaQuestion = {
      id: questionData.id,
      questionText: questionData.question,
      isCorrect: (guess: string) => guess === questionData.answer,
      photo: questionData.photo,
      caption: questionData.caption,
    };

    chosenQuestions.push(question);
  }
  return chosenQuestions;
}

class QuestionRange {
  start: number; // inclusive
  end: number; // exclusive

  constructor(start: number, end: number) {
    this.start = start;
    this.end = end;
  }
}

const QUESTION_RANGES = [
  new QuestionRange(0, 5),
  new QuestionRange(5, 10),
  new QuestionRange(10, 15),
  new QuestionRange(15, 20),
  new QuestionRange(20, 25),
];

const QUESTION_BANK = [
  {
    id: "1",
    question: "Who baked a wild blackberry pie at summer camp?",
    answer: "Liam",
    photo: "/assets/2024/liam/campers_and_pie_at_table.jpg",
    caption: "The Campers and our pie",
  },
  {
    id: "2",
    question: "Who ran all the way around Manhattan?",
    answer: "Liam",
    photo: "/assets/2024/liam/run_around_manhattan_with_runners.jpg",
    caption: "Only these friends could make this worth the pain",
  },
  {
    id: "3",
    question: "Who became a half uncle?",
    answer: "Liam",
    photo: "/assets/2024/liam/half_uncle.jpg",
    caption: "Liam, Molly, Sarah, and Baby Logan",
  },
  {
    id: "4",
    question: "Who MCed Ashwin and Divya's San Francisco wedding?",
    answer: "Liam",
    photo: "/assets/2024/liam/mced_wedding.jpg",
    caption: "Congratulations Ashwin and Divya!",
  },
  {
    id: "5",
    question: "Who backpacked in the Shasta-Trinity forest?",
    answer: "Liam",
    photo: "/assets/2024/liam/backpacking_shasta_trinity_with_friends.jpg",
    caption: "Watch out for the deer!",
  },
  {
    id: "6",
    question: "Who dressed up as a raccoon for Portola?",
    answer: "Myron",
    photo: "/assets/2024/myron/raccoons.jpg",
    caption: "I got cooked",
  },
  {
    id: "7",
    question: "Who was most brat?",
    answer: "Myron",
    photo: "/assets/2024/myron/banger_albums.png",
    caption: "Banger albums only",
  },
  {
    id: "8",
    question: "Who found King Kong and Elmo in Times Square?",
    answer: "Myron",
    photo: "/assets/2024/myron/elmo_times_square.png",
    caption: "Making new friends",
  },
  {
    id: "9",
    question: "Who ran a 5k at a music festival?",
    answer: "Myron",
    photo: "/assets/2024/myron/5k_at_music_festival.png",
    caption: "Music festivals are for more than just music",
  },
  {
    id: "10",
    question: "Who fell in love with horses?",
    answer: "Myron",
    photo: "/assets/2024/myron/horsegiirrl.gif",
    caption: "I get it now",
  },
  {
    id: "11",
    question: "Who met ice cream cows in Amherst?",
    answer: "Max",
    photo: "/assets/2024/max/amherst.png",
    caption: "Moo!",
  },
  {
    id: "12",
    question: "Who's favorite set at Portola was Justice?",
    answer: "Max",
    photo: "/assets/2024/max/portola.png",
    caption: "New heights",
  },
  {
    id: "13",
    question: "Who beat Mischief in Ultimate Frisbee?",
    answer: "Max",
    photo: "/assets/2024/max/donuts.png",
    caption: "Donuts are the key to victory",
  },
  {
    id: "14",
    question: "Who backpacked in the Desolation Wilderness?",
    answer: "Max",
    photo: "/assets/2024/max/desolation.png",
    caption: "Great views, better company",
  },
  {
    id: "15",
    question: "Who bartended their own party?",
    answer: "Max",
    photo: "/assets/2024/max/bartender.png",
    caption: "Career pivot?",
  },
  {
    id: "16",
    question: "Who celebrated Friendsgiving in NYC with his sister?",
    answer: "Glenn",
    photo: "/assets/2024/glenn/beer.png",
    caption: "Too much foam!",
  },
  {
    id: "17",
    question: "Who's favorite set at Portola was Jamie xx?",
    answer: "Glenn",
    photo: "/assets/2024/glenn/portola.png",
    caption: "XXtra fun",
  },
  {
    id: "18",
    question: "Who celebrated Fourth of July in NYC",
    answer: "Glenn",
    photo: "/assets/2024/glenn/subway.png",
    caption: "Eat fresh",
  },
  {
    id: "19",
    question: "Who ran a marathon in Minnesota?",
    answer: "Glenn",
    photo: "/assets/2024/glenn/marathon.png",
    caption: "26.2 miles of rain",
  },
  {
    id: "20",
    question: "Who solo camped in Pinnacles National Park?",
    answer: "Glenn",
    photo: "/assets/2024/glenn/pinnacles.png",
    caption: "Self-care",
  },
  {
    id: "21",
    question: "Who hiked in Waimea canyon barefoot?",
    answer: "Jace",
    photo: "/assets/2024/jace/waimea.png",
    caption: "Grounding",
  },
  {
    id: "22",
    question: "Who made rings in Tokyo?",
    answer: "Jace",
    photo: "/assets/2024/jace/rings.png",
    caption: "Wish Ryan had proposed...",
  },
  {
    id: "23",
    question: "Who built a sandcastle with an arch you could crawl through?",
    answer: "Jace",
    photo: "/assets/2024/jace/sandcastle.png",
    caption: "Catch me at the beach",
  },
  {
    id: "24",
    question: "Who binge-watched all of Haikyu in a 2 week span?",
    answer: "Jace",
    photo: "/assets/2024/jace/haikyu.png",
    caption: "Prep for Natties",
  },
  {
    id: "25",
    question: "Who achieved the Tuckinator in the Wingspan mobile app?",
    answer: "Jace",
    photo: "/assets/2024/jace/tuckinator.png",
    caption: "No expansions enabled",
  },
];
